import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue";

export type TUseGenericQueryKeyOptions = {
  /**
   * When false, only the base key segments are used (same as `justQueryKey` on list/detail).
   * @default true
   */
  includeSegment?: boolean;
};

/**
 * Normalizes a filters segment so the query key reflects the *request identity*,
 * i.e. it collapses to the same value whenever the serialized request URL would be
 * the same. This mirrors `filtersToQueryString` (src/api/shared/filters.ts): values
 * that are dropped from the URL must also be dropped from the key, otherwise two
 * identical requests get different keys, miss the cache, and fire duplicate GETs.
 *
 * Dropped (same as the URL serializer): null/undefined, empty/whitespace strings,
 * empty arrays, and the `enabled` flag (which is a query control, never a URL param).
 */
function normalizeSegment(segment: unknown): unknown {
  if (segment == null || typeof segment !== "object" || Array.isArray(segment)) {
    return segment;
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(segment as Record<string, unknown>)) {
    if (value == null) continue;
    if (key === "enabled") continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out;
}

/**
 * Builds a reactive TanStack Query key from a base key and an optional segment (filters, id, …).
 * Segment values are unwrapped to plain data so mounted queries share the same key shape as
 * `createQueryKeys()` / `buildQueryKey()` (e.g. `MyQueryKeys.list({ patientId })` for refetch).
 */
export function useGenericQueryKey(
  baseKey: MaybeRefOrGetter<readonly unknown[]>,
  segment?: MaybeRefOrGetter<unknown>,
  options?: TUseGenericQueryKeyOptions
): ComputedRef<readonly unknown[]> {
  const includeSegment = computed(() => options?.includeSegment ?? true);

  return computed(() => {
    const key = [...toValue(baseKey)];
    if (!includeSegment.value || segment === undefined) {
      return key;
    }
    return [...key, normalizeSegment(toValue(segment))];
  });
}
